/* eslint-disable max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import $ from "jquery";
import { get, omit } from "lodash";
import React from "react";
import { SelectProps } from "@mui/material";
import { authorizeForRole } from "components/auth/authorize-for-role";
import logger from "services/logger";
import { parseDumbInterestForm } from "utils/interest";
import { useSnackbar } from "components/haven-design/snackbar";
import { LoadingSpinner } from "components/graphql-views";
import { formMetaData, formMetaDataVariables, getViewerInterestId, getViewerInterestIdVariables, interestUpdateContainer, interestUpdateContainerVariables, UserRole } from "types/gql-types";
import InterestUpdateView from "./interest-update";
import { GET_VIEWER_INTEREST_ID, QUERY_FORM_METADATA, UPDATE_INTEREST_DATA } from "./queries";

const Container:React.FC = () => {
  const { interestId, interestListId } =
    useParams<{ interestId: string; interestListId: string }>();
  const { data, loading } = useQuery<
  getViewerInterestId,
  getViewerInterestIdVariables
  >(GET_VIEWER_INTEREST_ID, {
    variables: { interestListId: interestListId ?? "" },
  });
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();

  if (!interestId && !interestListId) {
    openSnackbar({ message: "There was an error while loading the application. Please try again.", status: "alert" });
    navigate("/dash/client/submissions");
    return null;
  }

  const queryMetadataVariables = {
    interestListID: interestListId ?? "",
    interestID: interestId ?? "",
  };
  const query =
    useQuery<formMetaData, formMetaDataVariables>(QUERY_FORM_METADATA, {
      variables: queryMetadataVariables,
      skip: !interestId,
    });
  const [update] =
    useMutation<interestUpdateContainer, interestUpdateContainerVariables>(UPDATE_INTEREST_DATA);

  if (query.loading || loading) {
    return <LoadingSpinner />;
  }

  if (!interestId && data && data.viewerInterestId) {
    navigate(`/dash/client/submissions/${interestListId}/${data.viewerInterestId}`);
  }

  if (!query.data) return null;

  const { data: { interest } } = query;

  const handleChangeHousehold: SelectProps["onChange"] = (event: any) => {
    const variables = {
      interestID: interestId ?? "",
      data: { householdCount: event.target.value },
    };
    update({ variables }).catch((err) => {
      logger.sendError(err);
      openSnackbar({ message: "Update failed. Please try again.", status: "error" });
    });
  };

  const updateFormData = (event:React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = omit(parseDumbInterestForm($("form#interest-form")[0], interest), "temp-input");
    const variables = {
      interestID: interestId ?? "",
      data: formData,
    };

    update({ variables })
      .then(() => {
        openSnackbar({ message: "Update Success", status: "success" });
        window.scrollTo(0, 0);
        setTimeout(() => navigate("/dash/client/submissions"), 100);
      })
      .catch(() => {
        openSnackbar({ message: "There was an error while updating the application. Please try again.", status: "alert" });
      });
  };

  return (
    <InterestUpdateView
      listId={interestListId}
      listName={get(query, ["data", "applicationFormMetadata", "interestListName"], "")}
      interestId={interestId}
      handleSubmit={updateFormData}
      handleChangeHousehold={handleChangeHousehold}
      formData={get(query, ["data", "interest"]) || {}}
      validationSchema={get(query, ["data", "applicationFormMetadata", "schema"], [])}
    />
  );
};

export default authorizeForRole([UserRole.CLIENT], Container);
